import { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useNavigate } from "react-router-dom";
import alignmentClasses from "utils/landing_page.json";
import { Button } from "components/Buttons";
import { handleRedirect } from "utils";

export const HeroSection = ({
  isCustomSaddle = false,
  heroData = {}
}) => {
  const navigate = useNavigate();
  const controlsScan = useAnimation();
  const controlsFit = useAnimation();
  const controlsPerform = useAnimation();

  const {
    content_head_first,
    content_head_second,
    content_head_third,
    content_description,
  } = alignmentClasses;

  const title1 = isCustomSaddle ? (
    <>
      Your <span style={{ color: "#7aa0d8" }}>{heroData.titleAccent}</span>
    </>
  ) : content_head_first;

  const title2 = isCustomSaddle ? heroData.subtitle : content_head_second;
  const title3 = isCustomSaddle ? "" : content_head_third;
  const desc = isCustomSaddle ? heroData.description : content_description;

  useEffect(() => {
    const sequence = async () => {
      await controlsScan.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: 0 },
      });
      await controlsFit.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: 0.1 },
      });
      await controlsPerform.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: 0 },
      });
    };
    sequence();
  }, [controlsScan, controlsFit, controlsPerform]);

  return (
    <div className={`flex px-[22px] sm:px-2 xl:px-10 2xl:px-0 ${isCustomSaddle ? 'w-full' : 'w-[95%]'} pb-[100px] xl:max-w-[1300px] mx-auto justify-normal`}>
      <div className={isCustomSaddle
        ? "w-full mx-auto items-start flex flex-col lg:items-start lg:mx-0 lg:w-auto"
        : "md:w-auto w-full md:mx-0 mx-auto items-center flex flex-col md:items-start"
      }>

        {isCustomSaddle && heroData.badge && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={controlsScan}
            className="inline-block"
          >
            <span
              className="px-6 py-2 !h-[40px] rounded-full border text-sm font-medium tracking-[-0.03em] font-montserrat"
              style={{
                color: "#7AA0D8",
                borderColor: "#33415B",
                background: "#2B364B69"
              }}
            >
              {heroData.badge}
            </span>
          </motion.div>
        )}

        <div className={isCustomSaddle
          ? "head_text m-0 text-start mt-4 sm:mt-[23px]"
          : "head_text flex md:text-start sm:text-center sm:flex-col md:flex-col m-0"
        }>
          {isCustomSaddle ? (
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={controlsScan}
              className="block !text-[36px] sm:!text-[56px] lg:!text-[72px] !tracking-[-0.03em] uppercase lg:!leading-[1.1] !leading-[1.2]"
              style={{ lineHeight: '1.2' }}
            >
              <div className="block">
                Your <span style={{ color: "#7aa0d8" }}>{heroData.titleAccent}</span>
              </div>
              <div className="block">
                {heroData.subtitle}
              </div>
            </motion.h1>
          ) : (
            <>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={controlsScan}
                className="block sm:leading-[90px] md:leading-[132px]"
              >
                {title1}
              </motion.h1>
              <div className="flex sm:flex-row md:flex-col">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={controlsFit}
                  className="block sm:leading-[90px] md:leading-[132px]"
                >
                  {title2}
                </motion.h2>
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  animate={controlsPerform}
                  className="block sm:leading-[90px] md:leading-[132px]"
                >
                  {title3}
                </motion.h3>
              </div>
            </>
          )}
        </div>

        <motion.span
          initial={{ opacity: 0, y: 70 }}
          animate={controlsScan}
          className={`perf_saddle_text min-h-[44.554px] ${isCustomSaddle ? 'mt-4 sm:mt-[23px] max-w-xl !text-[12px] sm:!text-[16px] text-start' : ''}`}
          style={isCustomSaddle ? {
            letterSpacing: "-0.48px",
            lineHeight: "1.2",
            fontWeight: 600,
            textTransform: 'none'
          } : {}}
        >
          {desc}
        </motion.span>

        {isCustomSaddle ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={controlsPerform}
            className="flex flex-col-reverse sm:flex-row gap-3 mt-4 sm:mt-[23px] items-center sm:items-start w-full sm:w-auto"
          >
            <Button
              size
              whiteOutline
              transparent
              className={"nav_outline_btn !w-full sm:max-w-[340px] sm:!w-[164px] !h-[48px] lg:!h-[52px] text-[14px] sm:!text-[16px] font-montserrat font-medium rounded-[56px] tracking-[-0.03em]"}
              onClick={() =>
                heroData.secondaryBtnUrl &&
                handleRedirect(heroData.secondaryBtnUrl, { navigate })
              }
            >
              {heroData.secondaryBtn}
            </Button>
            <Button
              size
              whiteOutline
              transparent
              className={"nav_outline_btn !w-full sm:max-w-[340px] sm:!w-[214px] !h-[48px] lg:!h-[52px] text-[14px] sm:!text-[16px] font-montserrat !font-medium rounded-[56px] !bg-[#2B364B] tracking-[-0.03em]"}
              onClick={() =>
                heroData.primaryBtnUrl &&
                handleRedirect(heroData.primaryBtnUrl, { navigate })
              }
            >
              {heroData.primaryBtn}
            </Button>
          </motion.div>
        ) : (
          <motion.div
            className="flex justify-center mt-[9px] lg:justify-start"
            initial={{ opacity: 0, y: 80 }}
            animate={controlsScan}
          ></motion.div>
        )}
      </div>
      <div className="btm_gradient" />
    </div>
  );
};
